import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, Save, Upload, Check, Palette } from 'lucide-react';
import { COLOR_PRESETS } from './constants';
import { AppleWalletPreview } from './AppleWalletPreview';

export const ColorsTab = ({ formData, handleChange, handleColorPreset, handleLogoUpload, handleSubmit, saving, isNew, uploading, getLogoUrl }) => (
  <div className="grid lg:grid-cols-[1fr,480px] gap-8">
    <Card className="border-slate-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Palette className="w-5 h-5" />
          Couleurs et logo
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Compact circle presets (point 2) */}
        <div className="space-y-2">
          <Label>Palettes</Label>
          <div className="flex flex-wrap gap-2">
            {COLOR_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() => handleColorPreset(preset)}
                className={`w-11 h-11 rounded-full relative overflow-hidden border-2 transition-all flex-shrink-0 ${
                  formData.primary_color === preset.primary
                    ? 'border-slate-900 ring-2 ring-offset-2 ring-slate-900'
                    : 'border-transparent hover:border-slate-300'
                }`}
                style={{ backgroundColor: preset.primary }}
                title={preset.name}
                data-testid={`color-preset-${preset.id}`}
              >
                {formData.primary_color === preset.primary && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Color pickers — native color + hex input */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="primary_color">Couleur de fond</Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                id="primary_color"
                name="primary_color"
                value={formData.primary_color}
                onChange={handleChange}
                className="w-10 h-10 rounded-lg cursor-pointer border border-slate-200 p-0.5"
                data-testid="card-primary-color"
              />
              <Input
                value={formData.primary_color}
                onChange={handleChange}
                name="primary_color"
                className="flex-1 font-mono text-sm h-10"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="secondary_color">Couleur du texte</Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                id="secondary_color"
                name="secondary_color"
                value={formData.secondary_color}
                onChange={handleChange}
                className="w-10 h-10 rounded-lg cursor-pointer border border-slate-200 p-0.5"
                data-testid="card-secondary-color"
              />
              <Input
                value={formData.secondary_color}
                onChange={handleChange}
                name="secondary_color"
                className="flex-1 font-mono text-sm h-10"
              />
            </div>
          </div>
        </div>

        {/* Logo — compact side-by-side (point 2) */}
        <div className="space-y-1.5">
          <Label>Logo</Label>
          <div className="flex items-center gap-3">
            {formData.logo_url ? (
              <img
                src={getLogoUrl(formData.logo_url)}
                alt="Logo"
                className="w-14 h-14 rounded-lg object-cover border border-slate-200 flex-shrink-0"
                data-testid="logo-preview"
              />
            ) : (
              <div className="w-14 h-14 rounded-lg bg-slate-100 flex items-center justify-center border border-slate-200 flex-shrink-0" data-testid="logo-placeholder">
                <Upload className="w-5 h-5 text-slate-400" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleLogoUpload}
                  className="hidden"
                  disabled={isNew || uploading}
                  data-testid="logo-upload-input"
                />
                <Button type="button" variant="outline" size="sm" disabled={isNew || uploading} className="w-full" asChild>
                  <span>
                    {uploading ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <Upload className="w-3 h-3 mr-1.5" />}
                    {formData.logo_url ? 'Changer' : 'Importer'}
                  </span>
                </Button>
              </label>
              <p className="text-[11px] text-slate-400 mt-1">480 x 150 px recommandé. PNG, JPG, WebP.</p>
              {isNew && <p className="text-[11px] text-orange-500 mt-0.5">Enregistrez la carte d'abord</p>}
            </div>
          </div>
        </div>

        <Button onClick={handleSubmit} className="w-full bg-blue-600 hover:bg-blue-700" disabled={saving} data-testid="save-colors-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer
        </Button>
      </CardContent>
    </Card>

    {/* Preview sticky + 20% (point 5) */}
    <div className="lg:sticky lg:top-5 self-start">
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle>Aperçu Apple Wallet</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="transform scale-[1.05] origin-top">
            <AppleWalletPreview formData={formData} getLogoUrl={getLogoUrl} variant="full" />
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
);
